import React, { useState } from 'react';
import styled from 'styled-components';
import { VscGraph } from 'react-icons/vsc';
import { FaPlay } from 'react-icons/fa';
import { DollarSign } from 'lucide-react';

import VideoModal from '../../../components/videoModal/VideoModal';

const HeroContainer = styled.section`
  position: relative;
  min-height: 90vh;
  padding: 150px 2rem 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background:
    radial-gradient(circle at 12% 18%, rgba(217, 102, 46, 0.14), transparent 42%),
    radial-gradient(circle at 88% 82%, rgba(0, 85, 134, 0.12), transparent 45%),
    var(--color-paper);

  @media screen and (max-width: 768px) {
    padding: 120px 1.5rem 60px;
    min-height: auto;
  }

  @media screen and (min-width: 1440px) {
    padding: 120px 1.5rem 40px;
    min-height: auto;
  }
`;

const Grid = styled.div`
  width: 100%;
  max-width: 1240px;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  align-items: center;
  gap: 2rem;

  @media screen and (max-width: 900px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
  
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;

  @media screen and (max-width: 768px){
    width: 80%;
    margin: 0 auto;
  }

  @media screen and (max-width: 480px){
    width: 95%;
    margin: 0 auto;
  }
`;

const Eyebrow = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--font-body);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-accent-hover);
  margin-bottom: 1.5rem;

  &::before {
    content: '';
    width: 28px;
    height: 2px;
    background: var(--color-accent);
  }

  @media screen and (max-width: 900px) {
    justify-content: center;
    text-align: start;
    font-size: 0.65rem;

    &::before {
      content: '';
      width: 0px;
      height: 0px;
      background: var(--color-accent);
    }
  }
`;

const HeroTitle = styled.h1`
  font-family: var(--font-display);
  font-weight: 700;
  font-size: clamp(2.4rem, 4.4vw, 3.6rem);
  line-height: 1.08;
  color: var(--color-brand-deep);
  margin-bottom: 1.5rem;
  max-width: 640px;

  @media screen and (max-width: 900px) {
    max-width: none;
    margin-left: auto;
    margin-right: auto;
    text-align: start;
  }
`;

const cycleDelays = ['0s', '2s', '4s', '6s', '8s'];

const HighlightWord = styled.span`
  animation: wordCycle 10s linear infinite;
  animation-delay: ${({ $delay }) => $delay};

  @keyframes wordCycle {
    0%, 16% { color: var(--color-accent); }
    20%, 100% { color: inherit; }
  }
`;

const HeroSubtitle = styled.p`
  font-family: var(--font-body);
  font-size: 1.15rem;
  line-height: 1.65;
  color: var(--color-ink-soft);
  max-width: 520px;
  margin-bottom: 2.5rem;

  @media screen and (max-width: 900px) {
    max-width: 560px;
    margin-left: auto;
    margin-right: auto;
    text-align: start;
  }
`;

const CTARow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;

  @media screen and (max-width: 900px) {
    justify-content: center;
  }
`;

const PrimaryButton = styled.a`
  background: var(--color-accent);
  color: var(--color-white);
  padding: 1rem 2rem;
  border-radius: var(--radius-sm);
  font-family: var(--font-body);
  font-size: 1rem;
  font-weight: 700;
  text-decoration: none;
  box-shadow: var(--shadow-soft);
  transition: transform 0.25s ease, background 0.25s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover {
    background: var(--color-accent-hover);
    transform: translateY(-3px);
  }

  @media screen and (max-width: 768px){
    width: 100%;
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  background: transparent;
  color: var(--color-brand-deep);
  border: 1.5px solid var(--color-border);
  padding: 0.95rem 1.75rem;
  border-radius: var(--radius-sm);
  font-family: var(--font-body);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;

  svg {
    width: 14px;
    height: 14px;
    fill: var(--color-accent);
  }

  &:hover {
    border-color: var(--color-accent);
    transform: translateY(-3px);
  }

  @media screen and (max-width: 768px){
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const ImageStage = styled.div`
  position: relative;
  height: 560px;
  display: flex;
  align-items: flex-end;
  justify-content: center;

  @media screen and (max-width: 900px) {
    display: none;
  }
`;

const BlobShape = styled.div`
  position: absolute;
  width: 420px;
  height: 420px;
  right: 6%;
  top: 4%;
  background: var(--color-brand-deep);
  border-radius: 42% 58% 63% 37% / 41% 45% 55% 59%;
  opacity: 0.9;
  z-index: 1;
`;

const HeroImage = styled.div`
  position: relative;
  width: 460px;
  height: 100%;
  z-index: 2;
  transform: translateX(-6%);
  background-image: url('/images/chef-portrait-image.2d902166ed494e413051.png');
  background-size: contain;
  background-position: bottom center;
  background-repeat: no-repeat;
  filter: drop-shadow(0 30px 40px rgba(11, 42, 61, 0.35));

  @media screen and (min-width: 1440px){
    transform: translateY(40px);
  }
`;

const FloatingBadge = styled.div`
  position: absolute;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-accent);
  border-radius: 50%;
  box-shadow: var(--shadow-soft);
  z-index: 3;
  animation: floatBadge 6s ease-in-out infinite;

  @keyframes floatBadge {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-14px); }
  }
`;

const MoneyBadge = styled(FloatingBadge)`
  top: 8%;
  right: 12%;
  animation-delay: 0.3s;
`;

const GraphBadge = styled(FloatingBadge)`
  bottom: 12%;
  left: 4%;
  background: var(--color-brand);
  animation-delay: 1.2s;
`;

const Hero = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  return (
    <HeroContainer id="home">
      <Grid>
        <HeroContent>
          <Eyebrow>Sistema de gestão para restaurantes e bares</Eyebrow>
          <HeroTitle>
            Transforme seu{' '}
            <HighlightWord $delay={cycleDelays[0]}>negócio</HighlightWord>,{' '}
            <HighlightWord $delay={cycleDelays[1]}>restaurante</HighlightWord>,{' '}
            <HighlightWord $delay={cycleDelays[2]}>bar</HighlightWord> ou{' '}
            <HighlightWord $delay={cycleDelays[3]}>delivery</HighlightWord>
          </HeroTitle>
          <HeroSubtitle>
            Chega de perder vendas por desorganização e processos manuais. Cardápio digital,
            controle de mesas, caixa e emissão fiscal — tudo em um só sistema.
          </HeroSubtitle>
          <CTARow>
            <PrimaryButton
              href="https://app.sofchef.com.br"
              target="_blank"
              data-cta-id="hero-primary-teste-gratis"
            >
              Testar grátis agora
            </PrimaryButton>
            <SecondaryButton
              type="button"
              data-cta-id="hero-secondary-ver-como-funciona"
              onClick={() => setIsVideoOpen(true)}
            >
              <FaPlay /> Ver como funciona
            </SecondaryButton>
          </CTARow>
        </HeroContent>

        <ImageStage>
          <BlobShape />
          <MoneyBadge>
            {/* <MdOutlineAttachMoney size={24} color="#fff" /> */}
            <DollarSign size={24} color="#fff" />
          </MoneyBadge>
          <GraphBadge>
            <VscGraph size={20} color="#fff" />
          </GraphBadge>
          <HeroImage role="img" aria-label="Chef sorrindo usando o sistema SofChef" />
        </ImageStage>
      </Grid>

      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoUrl="https://youtube.com/embed/Cxx26f3p6lY"
      />
    </HeroContainer>
  );
};

export default Hero;
